import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { createOnboardingHelpSession, reportOnboardingHelpEvent } from "../../../utils/onboardingHelpTelemetry";

// Guía de lectura: no crea, edita, confirma, anula ni migra movimientos.
// Telemetría exclusivamente estructural: se informan temas y opciones, nunca datos operativos.
const TOPICS = [
  {
    key: "types",
    title: "1. ¿Qué registra cada movimiento?",
    text: "Venta: lo que vendiste. Compra: lo que adquiriste, con recepción de stock cuando corresponda. Gasto: una salida que no es compra de mercadería. Cobro/pago: cancelaciones de saldos pendientes. Transferencia: cambia el dinero de lugar, no genera una venta. Ajustes: corrigen stock o saldos con trazabilidad.",
  },
  {
    key: "filters",
    title: "2. Barra superior y listado",
    text: "El buscador, los filtros de tipo, estado y fechas, y la paginación te ayudan a encontrar operaciones. Cada fila muestra el documento, momento, importe, tercero y estado. Podés consultar el detalle sin modificar nada.",
  },
  {
    key: "states",
    title: "3. Borrador, confirmado y anulado",
    text: "Un borrador permite revisar la operación antes de sus impactos definitivos. Al confirmar se registran sus efectos según el tipo: cuentas, caja, deuda o stock. Una anulación genera trazabilidad inversa; no se debe usar eliminar de raíz como corrección habitual.",
  },
  {
    key: "payments",
    title: "4. Ítems, impuestos y medios de cobro",
    text: "El ítem define qué se vende; el movimiento documenta cuándo y por cuánto. En el detalle mirás líneas, impuestos, pagos y pendientes. Una venta con Posnet puede mostrar bruto, comisión, financiación, impuesto sobre comisión y neto estimado: el neto no reemplaza el importe de venta.",
  },
];

export default function MovementOrientationDialog({ open, project, onClose, onNavigateStock, onNavigateCash }) {
  const helpSession = React.useRef(createOnboardingHelpSession());
  const [selectedHistory, setSelectedHistory] = React.useState("");
  React.useEffect(() => {
    if (open) {
      setSelectedHistory("");
      reportOnboardingHelpEvent(project, "movements_orientation_opened", {}, helpSession.current);
    }
  }, [open, project?.id, project?._id, project?.slug]);

  const reviewHistory = (choice) => {
    setSelectedHistory(choice);
    reportOnboardingHelpEvent(project, "historical_strategy_reviewed", { reasonCode: choice }, helpSession.current);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="wp-movements-guide-title">
      <DialogTitle id="wp-movements-guide-title" sx={{ fontWeight: 950 }}>
        Entendé Movimientos antes de operar
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info">
            Ítems es el catálogo de cosas que comprás o vendés. Movimientos es el historial de lo que ocurrió.
            No tenés que inventar operaciones para aprender a usar esta pantalla.
          </Alert>
          {TOPICS.map((topic) => (
            <Box key={topic.key} sx={{ borderBottom: "1px solid rgba(25,36,47,.09)", pb: 1.4 }}>
              <Typography sx={{ fontWeight: 950, mb: .4 }}>{topic.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>{topic.text}</Typography>
            </Box>
          ))}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 950 }}>¿Qué hago con mis comprobantes viejos?</Typography>
              <Chip label="Decisión de arranque" size="small" variant="outlined" />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, mb: 1.2 }}>
              Elegí una fecha de corte para empezar con stock y dinero reales. Las facturas fiscales emitidas siguen
              siendo documentos válidos; decidir el arranque del ERP no las vuelve a emitir ni las elimina.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button variant={selectedHistory === "fresh_start" ? "contained" : "outlined"} onClick={() => reviewHistory("fresh_start")} sx={{ textTransform: "none" }}>
                Empezar con saldos iniciales
              </Button>
              <Button variant={selectedHistory === "controlled_migration" ? "contained" : "outlined"} onClick={() => reviewHistory("controlled_migration")} sx={{ textTransform: "none" }}>
                Evaluar migrar comprobantes
              </Button>
            </Stack>
            {selectedHistory ? (
              <Alert severity="warning" sx={{ mt: 1.2 }}>
                {selectedHistory === "fresh_start"
                  ? "Recomendado si no necesitás reconstruir todo el pasado. Registrá existencias y saldos reales a una fecha, y conservá los comprobantes anteriores como respaldo. No los reingreses como ventas actuales."
                  : "Sólo si necesitás consultar documentos anteriores dentro del ERP. Planificá la importación para no duplicar stock, caja, deuda ni impuestos. La función 'Pasar a histórico' trabaja sobre movimientos ya existentes y requiere superadmin; no importa facturas externas."}
                {" "}Esta elección sólo muestra una explicación: no cambia datos ni guarda una configuración fiscal.
              </Alert>
            ) : null}
          </Box>
          <Divider />
          <Typography variant="body2" color="text.secondary">
            Siguiente paso sugerido: completar el conteo físico inicial si corresponde y configurar cada lugar de dinero.
            Después vas a poder registrar tu primera operación real.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: .7, p: 2 }}>
        {onNavigateStock ? <Button onClick={onNavigateStock} sx={{ textTransform: "none" }}>Ir a stock</Button> : null}
        {onNavigateCash ? <Button onClick={onNavigateCash} sx={{ textTransform: "none" }}>Ir a cajas</Button> : null}
        <Button variant="contained" onClick={onClose} sx={{ textTransform: "none", ml: "auto" }}>Entendido</Button>
      </DialogActions>
    </Dialog>
  );
}
